import { Button, Popconfirm, Form, Tabs, Input } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from 'dayjs';
import { cloneDeep } from "lodash";
import ReactQuill from 'react-quill';
import { FormConfigType } from "../../../components/editForm";
import { language, langPage, langCommon, langTableColumns } from "../../../locales";
import 'react-quill/dist/quill.snow.css';

const lang = langPage?.settings.notice;
const langHeader = language?.config.component.header;
const DEFAULT_LANGUAGES = [
    {
        Content: '',
        LanguageId: 1,
        Title: '',
        Name: langHeader?.cnZH,
    },
    {
        Content: '',
        LanguageId: 2,
        Title: '',
        Name: langHeader?.cnTW,
    },
    {
        Content: '',
        LanguageId: 3,
        Title: '',
        Name: langHeader?.enUS,
    }
]

export const generateTableColumns = (props: any): ColumnsType => {
    const { operationRef, loading, deleteData, } = props;

    return [
        {
            title: lang?.noticeName,
            dataIndex: "Name",
        },
        {
            title: lang?.IsPop,
            dataIndex: "IsPop",
            render(value) {
                return value === 1 ? langCommon?.yes : langCommon?.no
            },
        },
        {
            title: lang?.sort,
            dataIndex: "Sort",
        },
        {
            title: langCommon?.status,
            dataIndex: "Status",
            render(value) {
                return value === 1 ? lang?.display : lang?.hidden
            },
        },
        {
            title: langTableColumns?.CreatedAtString,
            dataIndex: "CreatedAtString",
        },
        {
            title: langTableColumns?.UpdatedAtString,
            dataIndex: "UpdatedAtString",
        },
        {
            title: langTableColumns?.operation,
            dataIndex: "ops",
            width: 120,
            fixed: "right",
            render: (_: any, row: any) => {
                const { Name, IsPop, NoticeLanguages, Sort, Status, Id } = row;
                return (
                    <div>
                        <Button size="small" type="link" className="pdl0"
                            onClick={() => operationRef?.current?.changeVisible({
                                Name, IsPop, Sort, Status, Id,
                                NoticeLanguages: cloneDeep(DEFAULT_LANGUAGES).map((e) => {
                                    const currentData = NoticeLanguages?.find((n: any) => n.LanguageId === e.LanguageId) || {}
                                    return {
                                        ...e,
                                        Title: currentData?.Title || '',
                                        Content: currentData?.Content || ''
                                    }
                                })
                            }, 'modify')}
                        >{langCommon?.modify}</Button>
                        <Popconfirm title={langCommon?.deleteItem} onConfirm={() => deleteData?.({ Id: row.Id })} >
                            <Button size="small" type="link" loading={loading} className="pdl0 pdr0">{langCommon?.delete}</Button>
                        </Popconfirm>
                    </div>
                )
            }
        }
    ]
}

export const generateLanguageTableColumns = (props: any): ColumnsType => {
    const { viewRef } = props;

    return [
        {
            title: lang?.noticeId,
            dataIndex: "NoticeId",
            align: "center"
        },
        {
            title: lang?.title,
            dataIndex: "Title",
            align: "center"
        },
        {
            title: lang?.language,
            dataIndex: "LanguageId",
            align: "center",
            render(value) {
                return value === 1 ? langHeader?.cnZH : value === 2 ? langHeader?.cnTW : langHeader?.enUS
            },
        },
        {
            title: langTableColumns?.CreatedAtString,
            dataIndex: "CreatedAt",
            align: "center",
            render(value, record, index) {
                return dayjs.unix(value).format('YYYY-MM-DD HH:mm:ss')
            },
        },
        {
            title: langTableColumns?.operation,
            dataIndex: "ops",
            align: "center",
            width: 120,
            fixed: "right",
            render: (_: any, row: any) => {
                return (
                    <div>
                        <Button size="small" type="link" className="pdl0"
                            onClick={() => viewRef?.current?.changeVisible(row)}
                        >{langCommon?.view}</Button>
                    </div>
                )
            }
        }
    ]
}

export const DEFAULT_OPERATION_FORM_DATA = {
    IsPop: 1,
    Name: null,
    Sort: 1,
    Status: 1,
    NoticeLanguages: [...DEFAULT_LANGUAGES]
}

export const generateOperationFormConfig = (props: any): FormConfigType => {

    return [
        {
            title: lang?.title,
            type: "input",
            field: "Name",
            allowClear: true,
            style: { width: 400 },
            rules: [{
                required: true,
                message: `${langCommon?.pleaseInput} ${lang?.title}`
            }]
        },
        {
            title: lang?.IsPop,
            type: "select",
            field: "IsPop",
            style: { width: 200 },
            options: [
                { id: 1, name: langCommon?.yes },
                { id: 2, name: langCommon?.no }
            ],
        },
        {
            title: langCommon?.status,
            type: "select",
            field: "Status",
            style: { width: 200 },
            options: [
                { id: 1, name: langCommon?.enabled },
                { id: 2, name: langCommon?.disable }
            ],
        },
        {
            title: lang?.sort,
            type: "inputNumber",
            field: "Sort",
            style: { width: 200 },
            rules: [{
                required: true,
                message: `${langCommon?.pleaseInput} ${lang?.sort}`
            }]
        },
        {
            title: lang?.language,
            type: "render",
            field: "NoticeLanguages",
            allowClear: true,
            required: true,
            rules: [{
                required: true,
                validator: async (_: any, value: any) => {
                    if (!Array.isArray(value)) {
                        return Promise.resolve();
                    }
                    const noIndex = value.findIndex((e: any) => !e.Content || e.Content === '<p><br></p>');
                    if (noIndex > -1) {
                        return Promise.reject(`${langCommon?.pleaseInput} ${value[noIndex].Name}${lang?.noticeS}`);
                    }
                    return Promise.resolve();
                }
            }],
            render: (formData: any, changeValue: any, form: any) => {

                return (
                    <Form.Item className="mt20">
                        <Tabs items={formData.NoticeLanguages?.map(((e: any, i: number) => ({
                            key: e.LanguageId.toString(),
                            label: e.Name,
                            children: (
                                <>
                                    <Form.Item label={lang?.noticeTitle} initialValue={formData.NoticeLanguages[i].Title}>
                                        <Input
                                            style={{ width: 360 }}
                                            defaultValue={formData.NoticeLanguages[i].Title}
                                            onChange={async (v) => {
                                                const { NoticeLanguages } = formData
                                                NoticeLanguages[i].Title = v.target.value;
                                                changeValue('NoticeLanguages', NoticeLanguages)
                                            }}
                                        />
                                    </Form.Item >

                                    {/* @ts-ignore */}
                                    <ReactQuill
                                        className="mt20"
                                        theme="snow"
                                        modules={{
                                            toolbar: [
                                                [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
                                                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                                [{ 'font': [] }],

                                                ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
                                                ['blockquote', 'code-block'],

                                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                [{ 'script': 'sub' }, { 'script': 'super' }],      // superscript/subscript
                                                [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent
                                                [{ 'direction': 'rtl' }],                         // text direction

                                                [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
                                                [{ 'align': [] }],

                                                ['image'],

                                                ['clean']                                         // remove formatting button
                                            ]
                                        }}
                                        value={formData.NoticeLanguages[i].Content}
                                        onChange={async (v) => {
                                            const { NoticeLanguages } = formData

                                            NoticeLanguages[i].Content = v;
                                            changeValue('NoticeLanguages', NoticeLanguages)
                                        }}
                                    />

                                </>
                            )
                        })))
                        } />
                    </Form.Item>
                )
            }
        },
    ];
}

